import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import SectionCard from '@/components/base/SectionCard';
import EmptyState from '@/components/base/EmptyState';
import ReceiptPreview from '@/pages/billing/components/ReceiptPreview';
import { useProject, useProjectEvents } from '@/hooks/useProjects';
import { useProjectReceipts } from '@/hooks/useReceipts';
import { useToast } from '@/components/feature/ToastProvider';
import { STATUS_CONFIG } from '@/lib/projectStatus';
import { getProjectAlerts } from '@/lib/alerts';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import { CONTRACTS_BUCKET, getSignedUrl } from '@/lib/storage';
import type { Receipt } from '@/types';

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-background-200 py-2.5 last:border-0">
      <span className="text-xs text-foreground-500">{label}</span>
      <span className="text-right text-sm font-medium text-foreground-900">{value}</span>
    </div>
  );
}

const EVENT_ICONS: Record<string, string> = {
  creacion: 'ri-add-circle-line',
  actualizacion: 'ri-edit-line',
};

async function forceDownload(url: string, filename: string) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { project, loading, error, reload } = useProject(id);
  const { receipts, loading: receiptsLoading } = useProjectReceipts(id);
  const { events, loading: eventsLoading } = useProjectEvents(id);
  const [previewReceipt, setPreviewReceipt] = useState<Receipt | null>(null);
  const [contractBusy, setContractBusy] = useState(false);

  if (loading) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50">
        <Loading label="Cargando proyecto..." />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (!project) {
    return (
      <EmptyState
        icon="ri-folder-warning-line"
        title="Proyecto no encontrado"
        description="El proyecto solicitado no existe o fue eliminado."
        action={
          <Button icon="ri-arrow-left-line" onClick={() => navigate('/proyectos')}>
            Volver a proyectos
          </Button>
        }
      />
    );
  }

  const config = STATUS_CONFIG[project.status];
  const alerts = getProjectAlerts(project, new Date(), receipts.map((receipt) => receipt.receipt_date));
  const client = project.clients;
  const totalBilled = receipts.reduce((sum, receipt) => sum + Number(receipt.amount), 0);

  const contractFileName = project.contract_document_url
    ? (() => {
        const last = project.contract_document_url.split('/').pop() ?? 'contrato.pdf';
        try {
          return decodeURIComponent(last);
        } catch {
          return last;
        }
      })()
    : 'contrato.pdf';

  const handleViewContract = async () => {
    if (!project.contract_document_url) return;
    setContractBusy(true);
    const url = await getSignedUrl(CONTRACTS_BUCKET, project.contract_document_url, 300);
    setContractBusy(false);
    if (url) window.open(url, '_blank', 'noopener');
    else notify({ tone: 'error', title: 'No se pudo abrir el contrato', message: 'Intente nuevamente.' });
  };

  const handleDownloadContract = async () => {
    if (!project.contract_document_url) return;
    setContractBusy(true);
    const url = await getSignedUrl(CONTRACTS_BUCKET, project.contract_document_url, 300);
    setContractBusy(false);
    if (url) await forceDownload(url, contractFileName);
    else notify({ tone: 'error', title: 'No se pudo descargar el contrato', message: 'Intente nuevamente.' });
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" icon="ri-arrow-left-line" onClick={() => navigate('/proyectos')}>
          Volver a proyectos
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-edit-line" onClick={() => navigate(`/proyectos/${project.id}/editar`)}>
            Editar
          </Button>
          <Button icon="ri-file-list-3-line" onClick={() => navigate('/facturacion')}>
            Ver facturación
          </Button>
        </div>
      </div>

      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            {project.logo_url ? (
              <img
                src={project.logo_url}
                alt={`Logo de ${project.name}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-foreground-400">
                <i className="ri-folder-3-line text-2xl"></i>
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-heading text-xl font-extrabold text-foreground-950">
                {project.name}
              </h2>
              <Badge tone={config.tone} icon={config.icon}>
                {config.label}
              </Badge>
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {project.description || 'Sin descripción registrada.'}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge tone="secondary" icon="ri-building-2-line">
                {client?.name || 'Cliente no asignado'}
              </Badge>
              {alerts.billingState === 'pendiente' && (
                <Badge tone="warning" icon="ri-alarm-warning-line">
                  <span className="font-bold">COBRO PENDIENTE</span>
                </Badge>
              )}
              {alerts.billingState === 'hoy' && (
                <Badge tone="danger" icon="ri-notification-3-line">
                  <span className="font-bold text-red-700">SE COBRA HOY</span>
                </Badge>
              )}
              {alerts.billingState === 'pagado' && (
                <Badge tone="success" icon="ri-checkbox-circle-line">
                  <span className="font-bold">PAGADO</span>
                </Badge>
              )}
              {alerts.contractState === 'por_finalizar' && (
                <Badge tone="danger" icon="ri-error-warning-line">
                  POR FINALIZAR
                </Badge>
              )}
              {alerts.contractState === 'finalizado' && (
                <Badge tone="danger" icon="ri-close-circle-line">
                  CONTRATO FINALIZADO
                </Badge>
              )}
              {!alerts.hasAlert && alerts.billingState !== 'pagado' && (
                <Badge tone="primary" icon="ri-checkbox-circle-line">
                  Sin alertas pendientes
                </Badge>
              )}
            </div>
            {project.link_proyecto && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground-600">
                  <i className="ri-links-line text-base"></i>
                  Página del proyecto
                </span>
                <a
                  href={project.link_proyecto}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-external-link-line text-base"></i>
                  </span>
                  Visitar página
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Próximo cobro</p>
          <p className="mt-1 font-heading text-lg font-bold text-foreground-950">
            {formatDate(alerts.nextBillingDate)}
          </p>
          <p className="text-xs text-foreground-500">
            {alerts.daysToBilling !== null ? `En ${alerts.daysToBilling} día(s)` : 'Día de cobro no definido'}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Fin de contrato</p>
          <p className="mt-1 font-heading text-lg font-bold text-foreground-950">
            {formatDate(project.contract_end)}
          </p>
          <p className="text-xs text-foreground-500">Estado del contrato: {
            alerts.contractState === 'finalizado'
              ? 'Finalizado'
              : alerts.contractState === 'por_finalizar'
                ? 'Por finalizar'
                : 'Vigente'
          }</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Recibos emitidos</p>
          <p className="mt-1 font-heading text-lg font-bold text-foreground-950">{receipts.length}</p>
          <p className="text-xs text-foreground-500">Historial de facturación</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Total facturado</p>
          <p className="mt-1 font-heading text-lg font-bold text-primary-700">
            {formatCurrency(totalBilled)}
          </p>
          <p className="text-xs text-foreground-500">Suma de recibos del proyecto</p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <SectionCard title="Desarrollo" icon="ri-code-s-slash-line">
          <InfoRow label="Inicio del desarrollo" value={formatDate(project.development_start)} />
          <InfoRow label="Fin del desarrollo" value={formatDate(project.development_end)} />
          <InfoRow label="Estado" value={config.label} />
        </SectionCard>

        <SectionCard title="Cliente" icon="ri-user-3-line">
          <InfoRow label="Nombre" value={client?.name || '—'} />
          <InfoRow label="Número de celular" value={client?.contact_phone || '—'} />
        </SectionCard>

        <SectionCard title="Facturación y cobro" icon="ri-calendar-check-line">
          <InfoRow
            label="Día de cobro mensual"
            value={project.billing_day ? `Día ${project.billing_day} de cada mes` : '—'}
          />
          <InfoRow
            label="Mensualidad"
            value={
              project.monthly_amount !== null && project.monthly_amount !== undefined
                ? formatCurrency(project.monthly_amount)
                : 'Monto no definido'
            }
          />
          <InfoRow label="Próximo cobro" value={formatDate(alerts.nextBillingDate)} />
          <InfoRow label="Total facturado" value={formatCurrency(totalBilled)} />
        </SectionCard>

        <SectionCard title="Contrato" icon="ri-file-shield-2-line">
          <InfoRow label="Inicio de contrato" value={formatDate(project.contract_start)} />
          <InfoRow label="Fin de contrato" value={formatDate(project.contract_end)} />
          <InfoRow
            label="Estado del contrato"
            value={
              alerts.contractState === 'finalizado'
                ? 'Finalizado'
                : alerts.contractState === 'por_finalizar'
                  ? 'Por finalizar'
                  : 'Vigente'
            }
          />
          <div className="mt-3">
            {project.contract_document_url ? (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-background-200 bg-background-100/60 p-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-red-50 text-red-600">
                  <i className="ri-file-pdf-2-line text-lg"></i>
                </span>
                <p className="min-w-0 flex-1 truncate text-sm text-foreground-700">{contractFileName}</p>
                <Button size="sm" variant="outline" icon="ri-eye-line" onClick={handleViewContract} disabled={contractBusy}>
                  Ver
                </Button>
                <Button size="sm" variant="outline" icon="ri-download-2-line" onClick={handleDownloadContract} disabled={contractBusy}>
                  Descargar
                </Button>
              </div>
            ) : (
              <p className="flex items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100/50 px-3 py-2.5 text-sm text-foreground-500">
                <i className="ri-file-3-line text-base"></i>
                No se cargó ningún documento de contrato.
              </p>
            )}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Historial de recibos" icon="ri-receipt-line">
        {receiptsLoading ? (
          <Loading label="Cargando recibos..." />
        ) : receipts.length === 0 ? (
          <EmptyState
            icon="ri-receipt-line"
            title="Sin recibos registrados"
            description="Este proyecto todavía no tiene recibos emitidos."
            action={
              <Button icon="ri-add-line" onClick={() => navigate('/facturacion')}>
                Ir a Facturación
              </Button>
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/70">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">N° Recibo</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Fecha</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Concepto</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Monto</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr key={receipt.id} className="border-b border-background-200 last:border-0">
                      <td className="px-4 py-3 text-sm font-semibold text-foreground-900">
                        {receipt.receipt_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">{formatDate(receipt.receipt_date)}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700">{receipt.concept}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-foreground-900">
                        {formatCurrency(receipt.amount)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setPreviewReceipt(receipt)}
                          title="Vista previa"
                          aria-label={`Vista previa del recibo ${receipt.receipt_number}`}
                          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <i className="ri-eye-line text-base"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Historial de cambios" icon="ri-history-line">
        {eventsLoading ? (
          <Loading label="Cargando historial..." />
        ) : events.length === 0 ? (
          <p className="text-sm text-foreground-500">Todavía no hay movimientos registrados para este proyecto.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {events.map((event) => (
              <li key={event.id} className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${EVENT_ICONS[event.event_type] ?? 'ri-history-line'} text-base`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-foreground-800">{event.description || event.event_type}</p>
                  <p className="text-xs text-foreground-500">{formatDateTime(event.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <ReceiptPreview receipt={previewReceipt} onClose={() => setPreviewReceipt(null)} />
    </div>
  );
}